import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import {
  getOrgDashboardActionItems,
  getOrgDashboardActivity,
  getOrgDashboardOverview,
  getOrgDashboardSigningMetrics,
  getOrgDashboardTeamSummary,
  getOrgDashboardTemplates,
  getOrgDashboardTrends,
  getOrgDashboardUsers,
} from '../../services/organizationDashboardService';
import { listOrganizationUsers } from '../../services/organizationUserService';
import TeamDashboardFilters from './TeamDashboardFilters';
import TeamTrendChart from './TeamTrendChart';
import TeamMiniMetric from './TeamMiniMetric';
import TeamStatusDonut from './TeamStatusDonut';
import TeamPanelHeader from './TeamPanelHeader';
import TeamActivityTimeline from './TeamActivityTimeline';
import TeamActionItemList from './TeamActionItemList';
import DashboardStatCard from '../dashboard/DashboardStatCard';
import MetricHelpIcon from '../dashboard/MetricHelpIcon';
import { TEAM_DASHBOARD_METRIC_HELP } from '../../constants/teamDashboardMetricHelp';
import {
  inferTrendGranularityFromSeries,
  resolveTrendGranularity,
  trendGranularityLabel,
} from '../../utils/trendGranularity';
import { getActiveOrganizationId } from '../../utils/organizations';

const EMPTY_EXTRA_FILTERS = {
  created_by_user_id: '',
  status: '',
  recipient_email: '',
  template_id: '',
};

function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)}%`;
}

function formatHours(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)} h`;
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function unwrapList(payload, keys = []) {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function formatPersonLabel(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    return value.name || value.email || (value.id != null ? `User ${value.id}` : '—');
  }
  return '—';
}

function formatTargetLabel(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    return value.name ?? value.subject ?? value.title ?? value.email ?? '';
  }
  return '';
}

function getUserInitials(name, email) {
  const source = String(name || email || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function MetricBar({ value = 0, max = 1, color = 'primary' }) {
  const pct = Math.min(100, Math.round((Number(value) / Math.max(1, max)) * 100));
  return (
    <div className="team-metric-bar">
      <span className="team-metric-bar__value">{value ?? 0}</span>
      <div className="team-metric-bar__track">
        <div className={`team-metric-bar__fill bg-${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function normalizeUserRow(row) {
  const user = row?.user && typeof row.user === 'object' ? row.user : row;
  return {
    id: user?.id ?? row?.id ?? row?.user_id,
    name: user?.name ?? row?.name,
    email: user?.email ?? row?.email,
    sent_count: row?.envelopes_sent ?? row?.sent_count ?? row?.sent ?? 0,
    completed_count: row?.completed_envelopes ?? row?.completed_count ?? row?.completed ?? 0,
    draft_count: row?.draft_envelopes ?? row?.draft_count ?? row?.drafts ?? 0,
    action_required_count: row?.action_required ?? row?.action_required_count ?? 0,
    avg_completion_hours: row?.avg_completion_hours,
    last_activity_at: row?.last_activity_at ?? row?.last_activity,
  };
}

const TeamDashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const activeOrganizationId = getActiveOrganizationId(user);

  const [dateRange, setDateRange] = React.useState({
    preset: 'all_time',
    startDate: '',
    endDate: '',
    query: {},
  });
  const [extraFilters, setExtraFilters] = React.useState(EMPTY_EXTRA_FILTERS);
  const [orgUsers, setOrgUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [trendsLoading, setTrendsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [userSort, setUserSort] = React.useState('sent');
  const [userPage, setUserPage] = React.useState(1);
  const [activityPage, setActivityPage] = React.useState(1);
  const [trendGranularityMode, setTrendGranularityMode] = React.useState('auto');
  const [highlightedUserId, setHighlightedUserId] = React.useState(null);

  const [overview, setOverview] = React.useState(null);
  const [trends, setTrends] = React.useState(null);
  const [usersData, setUsersData] = React.useState(null);
  const [actionItems, setActionItems] = React.useState([]);
  const [activity, setActivity] = React.useState(null);
  const [teamSummary, setTeamSummary] = React.useState(null);
  const [templates, setTemplates] = React.useState(null);
  const [signingMetrics, setSigningMetrics] = React.useState(null);

  const queryParams = React.useMemo(
    () => ({
      ...(dateRange.query || {}),
      ...Object.fromEntries(
        Object.entries(extraFilters).filter(([, value]) => value !== '' && value != null)
      ),
    }),
    [dateRange.query, extraFilters]
  );

  React.useEffect(() => {
    setUserPage(1);
    setActivityPage(1);
    setTrendGranularityMode('auto');
  }, [queryParams]);

  const isAllTimeRange = !dateRange.startDate && !dateRange.endDate;

  const effectiveTrendGranularity = React.useMemo(() => {
    if (trendGranularityMode !== 'auto') return trendGranularityMode;
    return resolveTrendGranularity({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  }, [trendGranularityMode, dateRange.startDate, dateRange.endDate]);

  const chartTrendGranularity = trends?.granularity || effectiveTrendGranularity;

  React.useEffect(() => {
    let alive = true;
    listOrganizationUsers()
      .then((data) => {
        if (!alive) return;
        const list = unwrapList(data, ['members', 'users']);
        setOrgUsers(list);
      })
      .catch(() => {
        if (!alive) return;
        setOrgUsers([]);
      });
    return () => {
      alive = false;
    };
  }, [activeOrganizationId]);

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const base = { ...queryParams };
        const [
          overviewRes,
          usersRes,
          actionItemsRes,
          activityRes,
          teamSummaryRes,
          templatesRes,
          signingRes,
        ] = await Promise.all([
          getOrgDashboardOverview(base),
          getOrgDashboardUsers({
            ...base,
            sort: userSort,
            direction: 'desc',
            page: userPage,
            per_page: 10,
          }),
          getOrgDashboardActionItems({ ...base, limit: 20 }),
          getOrgDashboardActivity({ ...base, page: activityPage, per_page: 15 }),
          getOrgDashboardTeamSummary(base),
          getOrgDashboardTemplates(base),
          getOrgDashboardSigningMetrics(base),
        ]);
        if (!alive) return;
        setOverview(overviewRes);
        setUsersData(usersRes);
        setActionItems(unwrapList(actionItemsRes, ['items', 'action_items']));
        setActivity(activityRes);
        setTeamSummary(teamSummaryRes);
        setTemplates(templatesRes);
        setSigningMetrics(signingRes);
      } catch (err) {
        if (!alive) return;
        if (err?.response?.status === 403) {
          setError('Organization admin access is required for the team dashboard.');
        } else {
          setError(err?.response?.data?.message || err?.message || 'Failed to load team dashboard.');
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [queryParams, userSort, userPage, activityPage, activeOrganizationId]);

  React.useEffect(() => {
    let alive = true;
    const loadTrends = async () => {
      setTrendsLoading(true);
      try {
        const base = { ...queryParams };
        let granularity = effectiveTrendGranularity;

        if (trendGranularityMode === 'auto' && isAllTimeRange) {
          const initial = await getOrgDashboardTrends({ ...base, granularity: 'day' });
          if (!alive) return;
          const series = initial?.series || initial || {};
          const inferred = inferTrendGranularityFromSeries(series, 'day');
          if (inferred === 'month') {
            const monthly = await getOrgDashboardTrends({ ...base, granularity: 'month' });
            if (!alive) return;
            setTrends(monthly);
            return;
          }
          setTrends(initial);
          return;
        }

        const trendsRes = await getOrgDashboardTrends({ ...base, granularity });
        if (!alive) return;
        setTrends(trendsRes);
      } catch (err) {
        if (!alive) return;
        setTrends(null);
      } finally {
        if (alive) setTrendsLoading(false);
      }
    };
    loadTrends();
    return () => {
      alive = false;
    };
  }, [queryParams, effectiveTrendGranularity, trendGranularityMode, isAllTimeRange, activeOrganizationId]);

  const summary = overview?.summary || {};
  const quota = overview?.quota || {};
  const statusBreakdown = overview?.status_breakdown || [];
  const userRows = unwrapList(usersData, ['users', 'data']);
  const usersPagination = usersData?.pagination || usersData?.meta || usersData || {};
  const normalizedUserRows = React.useMemo(
    () => userRows.map((row) => normalizeUserRow(row)),
    [userRows]
  );
  const userMetricMax = React.useMemo(() => ({
    drafts: Math.max(1, ...normalizedUserRows.map((u) => u.draft_count || 0)),
    sent: Math.max(1, ...normalizedUserRows.map((u) => u.sent_count || 0)),
    completed: Math.max(1, ...normalizedUserRows.map((u) => u.completed_count || 0)),
  }), [normalizedUserRows]);
  const activityRows = unwrapList(activity, ['activity', 'events', 'items']);
  const templateRows = unwrapList(templates, ['templates', 'items']);
  const team = teamSummary?.team && typeof teamSummary.team === 'object'
    ? teamSummary.team
    : teamSummary || {};
  const roleBreakdown = team.role_breakdown || team.roles || teamSummary?.role_breakdown || [];

  const envelopeKpiCards = [
    { label: 'Draft envelopes', value: summary.draft_envelopes, icon: 'ri-file-edit-line', color: 'primary', helpText: TEAM_DASHBOARD_METRIC_HELP.draft_envelopes },
    { label: 'Sent envelopes', value: summary.sent_envelopes, icon: 'ri-send-plane-line', color: 'info', helpText: TEAM_DASHBOARD_METRIC_HELP.sent_envelopes },
    { label: 'Completed envelopes', value: summary.completed_envelopes, icon: 'ri-checkbox-circle-line', color: 'success', helpText: TEAM_DASHBOARD_METRIC_HELP.completed_envelopes },
    { label: 'Action required', value: summary.action_required, icon: 'ri-alert-line', color: 'warning', helpText: TEAM_DASHBOARD_METRIC_HELP.action_required },
    { label: 'Revoked', value: summary.revoked_envelopes, icon: 'ri-close-circle-line', color: 'secondary', helpText: TEAM_DASHBOARD_METRIC_HELP.revoked_envelopes },
    { label: 'Declined', value: summary.declined_envelopes, icon: 'ri-thumb-down-line', color: 'danger', helpText: TEAM_DASHBOARD_METRIC_HELP.declined_envelopes },
    { label: 'Stuck envelopes', value: summary.stuck_envelopes, icon: 'ri-time-line', color: 'dark', helpText: TEAM_DASHBOARD_METRIC_HELP.stuck_envelopes },
    { label: 'Saved templates', value: summary.saved_templates, icon: 'ri-file-list-3-line', color: 'secondary', helpText: TEAM_DASHBOARD_METRIC_HELP.saved_templates },
  ];

  const performanceKpiCards = [
    { label: 'Organization users', value: summary.organization_users, icon: 'ri-team-line', color: 'primary', helpText: TEAM_DASHBOARD_METRIC_HELP.organization_users },
    { label: 'Completion rate', value: formatPercent(summary.completion_rate), icon: 'ri-percent-line', color: 'success', helpText: TEAM_DASHBOARD_METRIC_HELP.completion_rate },
    { label: 'Avg time to complete', value: formatHours(summary.avg_completion_hours), icon: 'ri-timer-line', color: 'info', helpText: TEAM_DASHBOARD_METRIC_HELP.avg_time_to_complete },
    {
      label: 'Quota used',
      value: quota.limit == null ? `${quota.used ?? 0} sent` : `${quota.used ?? 0} / ${quota.limit}`,
      icon: 'ri-pie-chart-2-line',
      color: 'primary',
      helpText: TEAM_DASHBOARD_METRIC_HELP.quota_used,
    },
  ];

  const signing = signingMetrics?.signing && typeof signingMetrics.signing === 'object'
    ? signingMetrics.signing
    : signingMetrics || {};
  const otp = signing.otp || signing.otp_stats || {};

  return (
    <div className="team-dashboard-page dashboard-page">
      <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between mb-4">
        <div>
          <h2 className="welcome-title mb-1">Team dashboard</h2>
          <p className="text-muted mb-0 small">
            Organization-wide analytics for admins
            {overview?.scope ? ` · ${overview.scope}` : ''}
          </p>
        </div>
        <Link to="/dashboard" className="text-decoration-none small">
          <i className="ri-arrow-left-line me-1" />
          Back to home
        </Link>
      </div>

      <Card className="dashboard-date-range-card mb-4">
        <Card.Body>
          <TeamDashboardFilters
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            filters={extraFilters}
            onFiltersChange={setExtraFilters}
            orgUsers={orgUsers}
            disabled={loading}
          />
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="mb-4">
        <p className="dashboard-stats-section-title mb-0">Envelope activity</p>
        <Row className="g-3 mt-1">
          {envelopeKpiCards.map((card) => (
            <Col key={card.label} xs={12} sm={6} lg={4} xl={3}>
              <DashboardStatCard
                label={card.label}
                value={card.value}
                icon={card.icon}
                color={card.color}
                loading={loading}
                helpText={card.helpText}
              />
            </Col>
          ))}
        </Row>
      </div>

      <div className="mb-4">
        <p className="dashboard-stats-section-title mb-0">Performance</p>
        <Row className="g-3 mt-1">
          {performanceKpiCards.map((card) => (
            <Col key={card.label} xs={12} sm={6} lg={3}>
              <DashboardStatCard
                label={card.label}
                value={card.value}
                icon={card.icon}
                color={card.color}
                loading={loading}
                helpText={card.helpText}
              />
            </Col>
          ))}
        </Row>
      </div>

      <p className="dashboard-stats-section-title mb-3">Insights</p>

      <Row className="mb-4 g-3">
        <Col lg={8}>
          <Card className="team-dashboard-panel team-dashboard-panel--accent h-100">
            <TeamPanelHeader
              title="Envelope trends"
              subtitle={`${trendGranularityLabel(chartTrendGranularity)} view${trends?.granularity && trends.granularity !== effectiveTrendGranularity ? ` · showing ${trends.granularity}` : ''}`}
              icon={{ className: 'ri-line-chart-line', color: 'primary' }}
              helpText={TEAM_DASHBOARD_METRIC_HELP.envelope_trends}
            >
              <Form.Select
                size="sm"
                style={{ width: '140px' }}
                value={trendGranularityMode}
                disabled={trendsLoading}
                onChange={(e) => setTrendGranularityMode(e.target.value)}
              >
                <option value="auto">Auto</option>
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
              </Form.Select>
            </TeamPanelHeader>
            <Card.Body>
              {trendsLoading ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
              ) : (
                <TeamTrendChart
                  series={trends?.series || trends || {}}
                  granularity={chartTrendGranularity}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="team-dashboard-panel team-dashboard-panel--accent h-100">
            <TeamPanelHeader
              title="Status breakdown"
              icon={{ className: 'ri-pie-chart-2-line', color: 'info' }}
              helpText={TEAM_DASHBOARD_METRIC_HELP.status_breakdown}
            />
            <Card.Body>
              {loading ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
              ) : (
                <TeamStatusDonut items={statusBreakdown} />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <p className="dashboard-stats-section-title mb-3">Team &amp; signing</p>

      <Row className="mb-4 g-3">
        <Col lg={6}>
          <Card className="team-dashboard-panel team-dashboard-panel--accent h-100">
            <TeamPanelHeader
              title="Team summary"
              icon={{ className: 'ri-team-line', color: 'primary' }}
            />
            <Card.Body>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <Row className="g-3 mb-3">
                    <Col xs={6}>
                      <TeamMiniMetric
                        label="Active users"
                        value={team.active_users ?? team.users_active}
                        icon="ri-user-star-line"
                        color="primary"
                        loading={loading}
                        helpText={TEAM_DASHBOARD_METRIC_HELP.active_users}
                      />
                    </Col>
                    <Col xs={6}>
                      <TeamMiniMetric
                        label="Pending invites"
                        value={team.pending_invites}
                        icon="ri-mail-send-line"
                        color="info"
                        loading={loading}
                        helpText={TEAM_DASHBOARD_METRIC_HELP.pending_invites}
                      />
                    </Col>
                    <Col xs={6}>
                      <TeamMiniMetric
                        label="Seats used"
                        value={team.seats_used}
                        suffix={team.seats_allowed != null ? `/ ${team.seats_allowed}` : null}
                        icon="ri-group-line"
                        color="success"
                        loading={loading}
                        helpText={TEAM_DASHBOARD_METRIC_HELP.seats_used}
                      />
                    </Col>
                    <Col xs={6}>
                      <TeamMiniMetric
                        label="Zero activity"
                        value={team.users_with_zero_activity ?? team.zero_activity_users}
                        icon="ri-user-unfollow-line"
                        color="warning"
                        loading={loading}
                        helpText={TEAM_DASHBOARD_METRIC_HELP.users_with_zero_activity}
                      />
                    </Col>
                  </Row>
                  {Array.isArray(roleBreakdown) && roleBreakdown.length > 0 && (
                    <div className="team-role-breakdown">
                      <p className="small text-muted mb-2 d-inline-flex align-items-center gap-1">
                        Role breakdown
                        <MetricHelpIcon text={TEAM_DASHBOARD_METRIC_HELP.role_breakdown} id="role-breakdown" />
                      </p>
                      {roleBreakdown.map((role) => {
                        const count = role.count ?? role.users ?? 0;
                        const totalRoles = roleBreakdown.reduce((sum, r) => sum + (r.count ?? r.users ?? 0), 0) || 1;
                        const pct = (count / totalRoles) * 100;
                        return (
                          <div key={role.role || role.name} className="team-role-breakdown__row">
                            <div className="d-flex justify-content-between small mb-1">
                              <span>{role.role || role.name}</span>
                              <span>{count}</span>
                            </div>
                            <div className="team-status-bar-track">
                              <div className="team-status-bar-fill" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="team-dashboard-panel team-dashboard-panel--accent h-100">
            <TeamPanelHeader
              title="Signing metrics"
              icon={{ className: 'ri-quill-pen-line', color: 'success' }}
            />
            <Card.Body>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Row className="g-3">
                  <Col xs={6}>
                    <TeamMiniMetric
                      label="Avg signatures / envelope"
                      value={signing.avg_signatures_per_envelope ?? signing.avg_signatures}
                      icon="ri-file-list-2-line"
                      color="primary"
                      helpText={TEAM_DASHBOARD_METRIC_HELP.avg_signatures_per_envelope}
                    />
                  </Col>
                  <Col xs={6}>
                    <TeamMiniMetric
                      label="Declines"
                      value={signing.decline_count ?? signing.declines ?? 0}
                      icon="ri-thumb-down-line"
                      color="danger"
                      helpText={TEAM_DASHBOARD_METRIC_HELP.decline_count}
                    />
                  </Col>
                  <Col xs={6}>
                    <TeamMiniMetric
                      label="OTP success"
                      value={otp.success ?? otp.success_count}
                      icon="ri-shield-check-line"
                      color="success"
                      helpText={TEAM_DASHBOARD_METRIC_HELP.otp_success}
                    />
                  </Col>
                  <Col xs={6}>
                    <TeamMiniMetric
                      label="OTP failures"
                      value={otp.failure ?? otp.failure_count}
                      icon="ri-shield-cross-line"
                      color="warning"
                      helpText={TEAM_DASHBOARD_METRIC_HELP.otp_failure}
                    />
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <p className="dashboard-stats-section-title mb-3">Productivity &amp; activity</p>

      <Card className="team-dashboard-panel team-dashboard-panel--accent mb-4">
        <TeamPanelHeader
          title="User productivity"
          subtitle="Click a row to highlight a team member"
          icon={{ className: 'ri-bar-chart-grouped-line', color: 'primary' }}
        >
          <Form.Select
            size="sm"
            style={{ width: '180px' }}
            value={userSort}
            disabled={loading}
            onChange={(e) => {
              setUserPage(1);
              setUserSort(e.target.value);
            }}
          >
            <option value="sent">Sort by sent</option>
            <option value="completed">Sort by completed</option>
            <option value="drafts">Sort by drafts</option>
            <option value="last_activity">Sort by last activity</option>
          </Form.Select>
        </TeamPanelHeader>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle team-user-table">
            <thead>
              <tr>
                <th className="ps-3">User</th>
                <th>
                  <span className="d-inline-flex align-items-center gap-1">
                    Drafts
                    <MetricHelpIcon text={TEAM_DASHBOARD_METRIC_HELP.user_drafts} id="user-drafts" />
                  </span>
                </th>
                <th>
                  <span className="d-inline-flex align-items-center gap-1">
                    Sent
                    <MetricHelpIcon text={TEAM_DASHBOARD_METRIC_HELP.user_sent} id="user-sent" />
                  </span>
                </th>
                <th>
                  <span className="d-inline-flex align-items-center gap-1">
                    Completed
                    <MetricHelpIcon text={TEAM_DASHBOARD_METRIC_HELP.user_completed} id="user-completed" />
                  </span>
                </th>
                <th>
                  <span className="d-inline-flex align-items-center gap-1">
                    Action required
                    <MetricHelpIcon text={TEAM_DASHBOARD_METRIC_HELP.user_action_required} id="user-action-required" />
                  </span>
                </th>
                <th>
                  <span className="d-inline-flex align-items-center gap-1">
                    Avg time to complete
                    <MetricHelpIcon text={TEAM_DASHBOARD_METRIC_HELP.user_avg_time_to_complete} id="user-avg-time" />
                  </span>
                </th>
                <th>
                  <span className="d-inline-flex align-items-center gap-1">
                    Last activity
                    <MetricHelpIcon text={TEAM_DASHBOARD_METRIC_HELP.user_last_activity} id="user-last-activity" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-4"><Spinner animation="border" size="sm" /></td></tr>
              ) : userRows.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted py-4">No user activity found.</td></tr>
              ) : (
                userRows.map((row) => {
                  const user = normalizeUserRow(row);
                  const userId = user.id;
                  const isHighlighted = highlightedUserId === userId;
                  return (
                    <tr
                      key={userId || user.email}
                      className={isHighlighted ? 'team-user-table__row is-highlighted' : 'team-user-table__row'}
                      onClick={() => setHighlightedUserId((prev) => (prev === userId ? null : userId))}
                    >
                      <td className="ps-3">
                        <div className="d-flex align-items-center gap-2">
                          <span className="team-user-avatar">{getUserInitials(user.name, user.email)}</span>
                          <div>
                            <div className="fw-medium">{formatPersonLabel(user)}</div>
                            {user.email && typeof user.email === 'string' && (
                              <small className="text-muted">{user.email}</small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td><MetricBar value={user.draft_count} max={userMetricMax.drafts} color="secondary" /></td>
                      <td><MetricBar value={user.sent_count} max={userMetricMax.sent} color="info" /></td>
                      <td><MetricBar value={user.completed_count} max={userMetricMax.completed} color="success" /></td>
                      <td>
                        {user.action_required_count > 0 ? (
                          <Badge bg="warning" text="dark">{user.action_required_count}</Badge>
                        ) : (
                          <span className="text-muted">0</span>
                        )}
                      </td>
                      <td>{user.completed_count > 0 ? formatHours(user.avg_completion_hours) : '—'}</td>
                      <td className="text-muted small">{formatDateTime(user.last_activity_at)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Card.Body>
        {!loading && (usersPagination?.last_page > 1) && (
          <Card.Footer className="d-flex justify-content-between align-items-center bg-white">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={userPage <= 1}
              onClick={() => setUserPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="small text-muted">Page {userPage}</span>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={userPage >= (usersPagination?.last_page ?? 1)}
              onClick={() => setUserPage((p) => p + 1)}
            >
              Next
            </Button>
          </Card.Footer>
        )}
      </Card>

      <Row className="mb-4 g-3">
        <Col lg={6}>
          <Card className="team-dashboard-panel team-dashboard-panel--accent h-100">
            <TeamPanelHeader
              title="Action items"
              subtitle="Envelopes that need attention"
              icon={{ className: 'ri-alarm-warning-line', color: 'warning' }}
            />
            <Card.Body className="p-3">
              {loading ? (
                <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
              ) : actionItems.length === 0 ? (
                <div className="team-empty-state">
                  <i className="ri-checkbox-circle-line" />
                  <p>All caught up — no action items.</p>
                </div>
              ) : (
                <TeamActionItemList items={actionItems} formatPersonLabel={formatPersonLabel} />
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="team-dashboard-panel team-dashboard-panel--accent h-100">
            <TeamPanelHeader
              title="Recent activity"
              icon={{ className: 'ri-history-line', color: 'info' }}
            />
            <Card.Body className="p-3">
              {loading ? (
                <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
              ) : activityRows.length === 0 ? (
                <div className="team-empty-state">
                  <i className="ri-pulse-line" />
                  <p>No recent activity.</p>
                </div>
              ) : (
                <TeamActivityTimeline
                  items={activityRows}
                  formatPersonLabel={formatPersonLabel}
                  formatTargetLabel={formatTargetLabel}
                  formatDateTime={formatDateTime}
                />
              )}
            </Card.Body>
            {!loading && (
              <Card.Footer className="d-flex justify-content-between bg-white">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={activityPage <= 1}
                  onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setActivityPage((p) => p + 1)}>
                  Next
                </Button>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      <p className="dashboard-stats-section-title mb-3">Templates</p>

      <Card className="team-dashboard-panel team-dashboard-panel--accent">
        <TeamPanelHeader
          title="Template analytics"
          icon={{ className: 'ri-file-copy-2-line', color: 'secondary' }}
        />
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle team-template-table">
            <thead>
              <tr>
                <th className="ps-3">Template</th>
                <th>Use count</th>
                <th>Completion rate</th>
                <th>Avg recipients</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-4"><Spinner animation="border" size="sm" /></td></tr>
              ) : templateRows.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-muted py-4">No template data.</td></tr>
              ) : (
                templateRows.map((row) => {
                  const rate = Number(row.completion_rate);
                  const ratePct = Number.isFinite(rate) ? Math.min(100, Math.max(0, rate)) : null;
                  return (
                    <tr key={row.id ?? row.template_id ?? row.name}>
                      <td className="ps-3 fw-medium">{row.name ?? row.template_name ?? '—'}</td>
                      <td>
                        <Badge bg="primary" className="team-template-table__count">{row.use_count ?? 0}</Badge>
                      </td>
                      <td>
                        {ratePct == null ? '—' : (
                          <div className="team-template-rate">
                            <span>{formatPercent(rate)}</span>
                            <div className="team-status-bar-track">
                              <div className="team-status-bar-fill" style={{ width: `${ratePct}%` }} />
                            </div>
                          </div>
                        )}
                      </td>
                      <td>{row.avg_recipients ?? templates?.avg_recipients_per_template ?? '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default TeamDashboard;
